import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { getFooterStyles } from "../theme/siteTheme";

export interface NavLink {
  href: string;
  label: string;
}

interface FooterProps {
  navLinks: NavLink[];
  legalLinks: NavLink[];
}

const Footer: React.FC<FooterProps> = ({ navLinks, legalLinks }) => {
  const theme = useTheme();
  const styles = getFooterStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [moreAnchorEl, setMoreAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  // Social links
  const socialLinks = [
    {
      href: "https://www.facebook.com/groups/1674067636217751/",
      label: "Facebook",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.406.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.406 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
        </svg>
      ),
    },
    {
      href: "https://www.instagram.com/wbl_dartliga/?utm_source=qr#",
      label: "Instagram",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.241-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.775.131 4.602.425 3.635 1.392 2.668 2.359 2.374 3.532 2.315 4.808 2.256 6.088 2.243 6.497 2.243 12c0 5.503.013 5.912.072 7.192.059 1.276.353 2.449 1.32 3.416.967.967 2.14 1.261 3.416 1.32 1.28.059 1.689.072 7.192.072s5.912-.013 7.192-.072c1.276-.059 2.449-.353 3.416-1.32.967-.967 1.261-2.14 1.32-3.416.059-1.28.072-1.689.072-7.192s-.013-5.912-.072-7.192c-.059-1.276-.353-2.449-1.32-3.416C21.449.425 20.276.131 19 .072 17.72.013 17.311 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
        </svg>
      ),
    },
  ];

  // Only show 3 items on mobile: Facebook, Instagram, and ... menu
  const visibleMobileLinks = [
    ...socialLinks,
    { label: "Mehr", icon: <MoreHorizIcon />, isMore: true as const },
  ];

  return (
    <Box
      component="footer"
      sx={styles.root}
    >
      <Box component="nav" sx={styles.nav}>
        {isMobile ? (
          <Box sx={styles.mobileGroup}>
            {visibleMobileLinks.map((item) => {
              if ("isMore" in item && item.isMore) {
                return (
                  <Button
                    key="more"
                    aria-label="Mehr"
                    onClick={handleMoreClick}
                    sx={styles.iconButton}
                  >
                    {item.icon}
                  </Button>
                );
              } else if ("href" in item) {
                return (
                  <Button
                    key={item.label}
                    color="inherit"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                    sx={styles.iconButton}
                  >
                    {item.icon}
                  </Button>
                );
              } else {
                return null;
              }
            })}
            <Menu
              anchorEl={moreAnchorEl}
              open={Boolean(moreAnchorEl)}
              onClose={handleMoreClose}
              keepMounted
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              PaperProps={{ sx: styles.menuPaper }}
            >
              {navLinks.map((link) => (
                <MenuItem
                  key={link.href}
                  onClick={handleMoreClose}
                  component="a"
                  href={link.href}
                  sx={styles.menuItem}
                >
                  {link.label}
                </MenuItem>
              ))}
              {legalLinks.map((link) => (
                <MenuItem
                  key={link.href}
                  onClick={handleMoreClose}
                  component="a"
                  href={link.href}
                  sx={styles.menuItem}
                >
                  {link.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : (
          <>
            <Box sx={styles.desktopLinks}>
              {navLinks.map((link) => (
                <Button key={link.href} href={link.href} sx={styles.linkButton}>
                  {link.label}
                </Button>
              ))}
              {legalLinks.map((link) => (
                <Button key={link.href} href={link.href} sx={styles.linkButton}>
                  {link.label}
                </Button>
              ))}
            </Box>
            <Box sx={styles.socialLinks}>
              {socialLinks.map((item) => (
                <Button
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                  sx={styles.iconButton}
                >
                  {item.icon}
                </Button>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Footer;
